import { GL, Program } from '../gl';

const VS = /* glsl */ `#version 300 es
precision highp float;
layout(location = 0) in vec2 a_corner;
uniform vec2 u_viewport;
uniform vec2 u_center;   // device px, origin bottom-left
uniform float u_half;    // quad half-size, device px
uniform float u_radius;  // disc radius, device px
out vec2 v_q;
void main() {
  vec2 pos = u_center + a_corner * u_half;
  v_q = a_corner * u_half / u_radius;
  gl_Position = vec4(pos / u_viewport * 2.0 - 1.0, 0.0, 1.0);
}`;

const FS = /* glsl */ `#version 300 es
precision highp float;
in vec2 v_q;
out vec4 o;
uniform int u_kind;          // 0 Sun, 1 Moon, 2 planet, 3 Saturn, 4 Jupiter
uniform vec3 u_light;        // towards the Sun, local frame (x right, y up, z to viewer)
uniform vec3 u_pole;         // body north pole, local frame
uniform vec3 u_color;        // linear tint (includes atmospheric reddening)
uniform float u_bright;      // disc brightness multiplier
uniform float u_glow;        // glow strength
uniform float u_glowSize;    // glow falloff, in disc radii
uniform float u_radiusPx;
uniform float u_starLike;    // 1 = unresolved: draw as a star point
uniform float u_earthshine;
uniform mat3 u_localToBody;
uniform sampler2D u_tex;
uniform float u_hasTex;
uniform float u_texAvg;      // planets: the map's average luminance, so it only adds detail to the tint
uniform sampler2D u_normal;  // Moon relief (tangent space: x east, y north, z up), from LOLA heights
uniform float u_hasNormal;
uniform vec2 u_shadow;       // Earth's shadow centre relative to the Moon's centre, in Moon radii
uniform float u_umbra;       // umbra and penumbra radii in Moon radii (0 = no eclipse)
uniform float u_penumbra;
const float PI = 3.14159265;

void main() {
  float r = length(v_q);
  vec3 col = vec3(0.0);
  float alpha = 0.0;
  float aa = 1.2 / max(u_radiusPx, 1.0);
  if (u_starLike < 0.99 && r < 1.0 + aa) {
    float edge = clamp((1.0 - r) / aa + 0.5, 0.0, 1.0);
    float z = sqrt(max(0.0, 1.0 - r * r));
    vec3 n = vec3(v_q, z);
    vec3 albedo = u_color;
    float lit;
    if (u_kind == 0) {
      lit = 1.0 - 0.55 * (1.0 - pow(z, 0.6)); // limb darkening
    } else if (u_kind == 1) {
      float mu0 = dot(n, u_light);
      vec3 b = u_localToBody * n;
      float lon = atan(b.y, b.x);
      float lat = asin(clamp(b.z, -1.0, 1.0));
      vec2 uv = vec2(0.5 + lon / (2.0 * PI), 0.5 - lat / PI);
      // Relief: tilt the surface by the local slope, so craters and mountains catch the low Sun at
      // the terminator (peaks just beyond it light up, far walls fall into shade).
      float muL = mu0;
      if (u_hasNormal > 0.5) {
        vec3 t = texture(u_normal, uv).xyz * 2.0 - 1.0;
        vec3 east = normalize(vec3(-sin(lon), cos(lon), 0.0));
        vec3 north = vec3(-sin(lat) * cos(lon), -sin(lat) * sin(lon), cos(lat));
        vec3 nb = normalize(t.x * east + t.y * north + t.z * b);
        muL = dot(transpose(u_localToBody) * nb, u_light) * smoothstep(-0.12, 0.02, mu0);
      }
      lit = muL > 0.0 ? 1.6 * muL / (muL + z + 1e-3) : 0.0; // Lommel–Seeliger
      if (u_hasTex > 0.5) {
        vec3 t = texture(u_tex, uv).rgb;
        albedo *= pow(t, vec3(2.2)) * 2.2;
      }
      lit += u_earthshine * (1.0 - smoothstep(-0.05, 0.1, mu0));
    } else {
      float mu0 = dot(n, u_light);
      lit = max(mu0, 0.0) * (0.55 + 0.45 * z) + 0.015;
      float lat = dot(n, u_pole);
      // Surface map (Mars, Mercury, Venus), turned to face us as it really does: its brightness
      // pattern on the planet's own colour, with bright areas (Mars's polar caps) going white.
      if (u_kind == 2 && u_hasTex > 0.5) {
        vec3 b = u_localToBody * n;
        vec3 t = pow(texture(u_tex, vec2(0.5 + atan(b.y, b.x) / (2.0 * PI), 0.5 - asin(clamp(b.z, -1.0, 1.0)) / PI)).rgb, vec3(2.2));
        float l = dot(t, vec3(0.2126, 0.7152, 0.0722)) / max(u_texAvg, 1e-3);
        vec3 grey = vec3(dot(albedo, vec3(0.2126, 0.7152, 0.0722)));
        albedo = mix(albedo, grey * 1.3, smoothstep(1.5, 2.4, l)) * l;
      }
      if (u_kind == 4) albedo *= 1.0 - 0.07 * smoothstep(0.35, 0.95, sin(lat * 13.0 + 0.6)) - 0.06 * smoothstep(0.85, 1.0, abs(lat));
      if (u_kind == 3) albedo *= 1.0 - 0.07 * smoothstep(0.4, 0.95, sin(lat * 10.0)) - 0.1 * smoothstep(0.8, 1.0, abs(lat));
    }
    col = albedo * lit * u_bright * edge;
    // Lunar eclipse: dimmed in the penumbra, deep copper in the umbra (sunlight bent through Earth's air).
    if (u_kind == 1 && u_penumbra > 0.0) {
      float d = length(v_q - u_shadow);
      float pen = mix(0.35, 1.0, smoothstep(u_umbra, u_penumbra, d));
      float um = 1.0 - smoothstep(u_umbra - 0.03, u_umbra + 0.03, d);
      vec3 copper = albedo * vec3(1.0, 0.4, 0.18) * 0.16 * (0.55 + 0.45 * smoothstep(0.0, u_umbra, d)) * u_bright * edge;
      col = mix(col * pen, copper, um);
    }
    alpha = edge;
  }
  // Saturn's rings: intersect the pixel's line of sight with the ring plane.
  if (u_kind == 3 && u_starLike < 0.99 && abs(u_pole.z) > 1e-3) {
    float zr = -(u_pole.x * v_q.x + u_pole.y * v_q.y) / u_pole.z;
    float rr = length(vec3(v_q, zr));
    bool behindPlanet = r < 1.0 && zr < sqrt(max(0.0, 1.0 - r * r));
    if (rr > 1.24 && rr < 2.27 && !behindPlanet) {
      float dens = 0.25 + 0.55 * smoothstep(1.5, 1.56, rr) * (1.0 - smoothstep(1.93, 1.96, rr)) + 0.3 * smoothstep(2.03, 2.05, rr) * (1.0 - smoothstep(2.2, 2.27, rr));
      dens *= 1.0 - 0.85 * smoothstep(1.955, 1.96, rr) * (1.0 - smoothstep(2.02, 2.03, rr)); // Cassini division
      float ringLit = 0.25 + 0.75 * abs(dot(u_pole, u_light));
      float ra = clamp(dens, 0.0, 1.0);
      col = mix(col, u_color * vec3(1.0, 0.95, 0.85) * ringLit * u_bright * 0.9, ra);
      alpha = max(alpha, ra);
    }
  }
  // Glow (and the whole look when unresolved).
  float g = 0.0;
  if (u_starLike > 0.0) {
    float s = r / max(u_glowSize, 1e-3);
    g += u_starLike * (exp(-s * s * 18.0) * 1.2 + exp(-s * s * 4.0) * 0.35);
  }
  if (u_glow > 0.0) {
    float d = max(r - 1.0, 0.0) / max(u_glowSize, 1e-3);
    g += u_glow * (exp(-d * 3.0) * 0.5 + 0.5 / (1.0 + d * d * 40.0)) * (r > 1.0 ? 1.0 : 0.6);
  }
  col += u_color * g;
  o = vec4(col, clamp(alpha, 0.0, 1.0));
}`;

export interface BodyDraw {
  kind: 0 | 1 | 2 | 3 | 4;
  centerX: number; // device px, origin bottom-left
  centerY: number;
  radiusPx: number;
  halfPx: number;
  light: [number, number, number];
  pole: [number, number, number];
  color: [number, number, number];
  bright: number;
  glow: number;
  glowSize: number;
  starLike: number;
  earthshine: number;
  localToBody?: Float32Array;
  /** Which planet map to use, once loaded. */
  texKey?: string;
  /** Lunar eclipse: shadow centre (Moon radii) and umbra/penumbra radii (Moon radii). */
  shadow?: [number, number, number, number];
}

export class BodyLayer {
  private prog: Program;
  private vao: WebGLVertexArrayObject;
  moonTex: WebGLTexture | null = null;
  /** Surface maps for planets, with each map's average luminance. */
  planetTex = new Map<string, { tex: WebGLTexture; avg: number }>();
  moonNormal: WebGLTexture | null = null;

  constructor(private gl: GL) {
    this.prog = new Program(gl, VS, FS, 'bodies');
    this.vao = gl.createVertexArray()!;
    gl.bindVertexArray(this.vao);
    const b = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, b);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.bindVertexArray(null);
  }

  draw(viewport: [number, number], items: BodyDraw[]) {
    const gl = this.gl;
    const p = this.prog.use();
    p.f2('u_viewport', viewport[0], viewport[1]);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.bindVertexArray(this.vao);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, this.moonTex);
    p.i1('u_tex', 2);
    gl.activeTexture(gl.TEXTURE4);
    gl.bindTexture(gl.TEXTURE_2D, this.moonNormal);
    p.i1('u_normal', 4);
    for (const it of items) {
      p.i1('u_kind', it.kind).f2('u_center', it.centerX, it.centerY).f1('u_half', it.halfPx).f1('u_radius', it.radiusPx);
      p.f1('u_radiusPx', it.radiusPx).v3('u_light', it.light).v3('u_pole', it.pole).v3('u_color', it.color);
      p.f1('u_bright', it.bright).f1('u_glow', it.glow).f1('u_glowSize', it.glowSize).f1('u_starLike', it.starLike);
      const pt = it.texKey ? this.planetTex.get(it.texKey) : undefined;
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, it.kind === 1 ? this.moonTex : pt?.tex ?? null);
      p.f1('u_earthshine', it.earthshine).f1('u_hasTex', it.kind === 1 ? (this.moonTex ? 1 : 0) : pt ? 1 : 0).f1('u_hasNormal', it.kind === 1 && this.moonNormal ? 1 : 0);
      p.f1('u_texAvg', pt?.avg ?? 1);
      p.m3('u_localToBody', it.localToBody ?? new Float32Array([1, 0, 0, 0, 1, 0, 0, 0, 1]));
      const sh = it.shadow ?? [0, 0, 0, 0];
      p.f2('u_shadow', sh[0], sh[1]).f1('u_umbra', sh[2]).f1('u_penumbra', sh[3]);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
    gl.bindVertexArray(null);
  }
}
